/**
 * Transforms a curriculum-service audienced course tree into the shape that
 * downstream components expect from the legacy `@cfc/course-manager-sdk`
 * `getCourseTree` pipeline.
 *
 * Legacy course tree (per node):
 *   {
 *     contentfulId,           // Contentful entry id
 *     name,                   // course name (e.g. 'K', '1', 'highschool')
 *     type,                   // 'course' | 'unit' | 'lesson' | 'choiceCurriculum' | ...
 *     children: [...],        // recursive
 *     leafNodeId,             // present on leaf lesson nodes
 *     requirementType,        // e.g. 'choice'
 *     childrenRequired,       // number of children required to satisfy parent
 *   }
 *
 * Curriculum-service tree node (`TreeNode`):
 *   {
 *     externalId,             // → contentfulId
 *     canonicalId,
 *     courseElementId,
 *     type,                   // course element version type key (already aligned)
 *     hierarchyTypeId,
 *     lessonContentItemId, presentationContentItemId,
 *     leafCompositeId,        // (kept as-is)
 *     presentationExternalId,
 *     displayTitle,
 *     depth,
 *     isLeaf,
 *     leafExternalId,         // → leafNodeId
 *     isPlaceholder,
 *     children: [...],
 *   }
 *
 * The transform copies all curriculum-service fields through and adds the
 * legacy aliases (`contentfulId`, `leafNodeId`) so existing downstream
 * helpers continue to work unchanged.
 */

function transformNode(node) {
  if (!node || typeof node !== 'object') return node

  const transformed = {
    ...node,
    contentfulId: node.contentfulId ?? node.externalId ?? null,
    leafNodeId:
      node.leafNodeId ?? node.leafExternalId ?? node.leafCompositeId ?? null,
  }

  if (Array.isArray(node.children)) {
    transformed.children = node.children.map(transformNode)
  }

  return transformed
}

/**
 * Transforms a single curriculum service course response into a legacy-shaped
 * tree object. The function accepts either:
 *   - a full POST /v1/audienced-course-versions response body (with `tree`),
 *     or
 *   - the bare `tree` node itself.
 *
 * Returns null when no tree is present.
 */
export function transformCurriculumCourseTree(input) {
  if (!input || typeof input !== 'object') return null

  // If the input is a full /v1/audienced-course-versions response, it has a
  // `tree` key. Treat the response wrapper as authoritative: if the wrapper
  // exists, use its (possibly null) tree.
  const isResponseWrapper = Object.prototype.hasOwnProperty.call(input, 'tree')
  const tree = isResponseWrapper ? input.tree : input

  if (!tree || typeof tree !== 'object') return null

  return transformNode(tree)
}

/**
 * Transforms a map of `{ [courseCode]: curriculumServiceResponse }` (as
 * produced by `selectCurriculumCourseTrees`) into a map of
 * `{ [courseCode]: legacyTree }` matching `useCourseManager`'s `courseTrees`
 * return value.
 */
export function transformCurriculumCourseTrees(curriculumCourseMap) {
  if (!curriculumCourseMap || typeof curriculumCourseMap !== 'object') {
    return {}
  }
  const trees = {}
  for (const [courseCode, payload] of Object.entries(curriculumCourseMap)) {
    const tree = transformCurriculumCourseTree(payload)
    if (tree) trees[courseCode] = tree
  }
  return trees
}
